import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { ResultsTable, CALC_PRINT_CSS } from "@/components/calc/ResultsTable";
import { ExportBar } from "@/components/calc/ExportBar";
import { CalcHistory } from "@/components/calc/CalcHistory";
import { recordCalculation } from "@/lib/calc/history";
import { stepsToText } from "@/lib/calc/steps";
import { tableToCsv, tableToTsv, type TableColumn } from "@/lib/calc/table";
import { parseNumber } from "./numberInput";
import { impliedTip, splitUneven, tipStrip, tipSteps, type StripRow, type TipInput, type TipResult } from "./tip";

/**
 * Under the Tip Calculator's result: comparison across percentages, uneven split, reverse mode, working and the gated export.
 * Loaded on demand (React.lazy).
 */
export default function TipExtras({ input, result, money, currency, gate }: { input: TipInput; result: TipResult; money: (n: number) => string; currency: string; gate: () => Promise<boolean>; }) {
  const [paid, setPaid] = useState("");
  const [items, setItems] = useState("");
  const steps = useMemo(() => tipSteps(input, result, money), [input, result, money]);
  const strip = useMemo(() => tipStrip({ bill: input.bill, tax: input.tax, people: input.people, tipOnPreTax: input.tipOnPreTax, digits: input.digits }), [input]);
  const cols: TableColumn<StripRow>[] = [
    { key: "pct", header: "Tip %", get: (r) => r.pct, format: (r) => `${r.pct}%`, align: "left" },
    { key: "tip", header: "Tip", get: (r) => r.tip, format: (r) => money(r.tip), align: "right" },
    { key: "total", header: "Total", get: (r) => r.total, format: (r) => money(r.total), align: "right" },
    { key: "per", header: input.people > 1 ? "Per person" : "You pay", get: (r) => r.perPerson, format: (r) => money(r.perPerson), align: "right" },
  ];
  const paidN = paid.trim() === "" ? null : parseNumber(paid);
  const rev = paidN === null ? null : impliedTip(input.bill, input.tax, paidN, input.tipOnPreTax);
  // Uneven split: one amount per person, separated by commas or spaces. Needs at least two people's amounts.
  const parts = items.split(/[\s,;]+/).filter(Boolean).map((x) => parseNumber(x));
  const itemsBad = items.trim() !== "" && (parts.some((x) => x === null || x < 0) || parts.length > 100);
  const uneven = items.trim() !== "" && !itemsBad && parts.length >= 2 ? splitUneven(parts as number[], input.tax, input.tipPct, input.tipOnPreTax, input.digits) : null;
  const unevenCols: TableColumn<{ n: number; s: ReturnType<typeof splitUneven>["shares"][number] }>[] = [
    { key: "n", header: "Person", get: (r) => r.n, align: "left" },
    { key: "own", header: "Own items", get: (r) => r.s.own, format: (r) => money(r.s.own), align: "right" },
    { key: "tax", header: "Tax share", get: (r) => r.s.taxShare, format: (r) => money(r.s.taxShare), align: "right" },
    { key: "tip", header: "Tip share", get: (r) => r.s.tipShare, format: (r) => money(r.s.tipShare), align: "right" },
    { key: "total", header: "Pays", get: (r) => r.s.total, format: (r) => money(r.s.total), align: "right" },
  ];
  const unevenRows = uneven ? uneven.shares.map((s, i) => ({ n: i + 1, s })) : [];

  const summary = `${money(input.bill)} bill, ${input.flatTip !== undefined ? `${money(input.flatTip)} flat tip` : `${input.tipPct}% tip`}: tip ${money(result.tip)}, total ${money(result.total)}`;
  useEffect(() => {
    const t = setTimeout(() => recordCalculation("tip-calculator", summary), 800);
    return () => clearTimeout(t);
  }, [summary]);
  const text = [
    "Tip calculation", summary, "", "How this was calculated", stepsToText(steps), "", "Compared at other percentages", tableToTsv(strip, cols),
    ...(uneven ? ["", "Uneven split", tableToTsv(unevenRows, unevenCols)] : []), "", `Currency: ${currency}. Calculated with FreeDailyPro.com`,
  ].join("\n");

  return (
    <div className="space-y-6 calc-print-area" data-testid="tip-extras">
      <style>{CALC_PRINT_CSS}</style>
      <section aria-label="Compare tip percentages" className="space-y-2">
        <h3 className="text-base font-semibold">Compare tip percentages</h3>
        <div data-testid="tip-strip"><ResultsTable caption="Tip, total and per-person amount at 10, 15, 18, 20 and 25 percent" rows={strip} columns={cols} rowKey={(r) => r.pct} maxHeightClass="max-h-72" /></div>
      </section>

      <section aria-label="Uneven split" className="space-y-2 no-print-inputs">
        <h3 className="text-base font-semibold">Uneven split</h3>
        <Label htmlFor="tip-items" className="text-sm">Each person's own items, separated by commas</Label>
        <Input id="tip-items" inputMode="decimal" placeholder="20, 30, 40" value={items} onChange={(e) => setItems(e.target.value)} aria-invalid={itemsBad ? true : undefined} />
        {itemsBad && <p role="alert" className="text-sm text-destructive" data-testid="tip-items-error">Use amounts of zero or more, like 20, 30, 40.</p>}
        {uneven && (
          <>
            <p className="text-xs text-muted-foreground">Each person pays their own amount plus their share of the tax and the {input.tipPct}% tip, in proportion to what they ordered. Tax and tip use the settings above.</p>
            <div data-testid="tip-uneven"><ResultsTable caption="Uneven split" rows={unevenRows} columns={unevenCols} rowKey={(r) => r.n} maxHeightClass="max-h-72" /></div>
            <p className="text-sm">Together they pay <strong>{money(uneven.total)}</strong> (items {money(uneven.itemsTotal)}, tip {money(uneven.tip)}).</p>
          </>
        )}
      </section>

      <section aria-label="Work out the tip you left" className="space-y-2">
        <h3 className="text-base font-semibold">What percentage did I tip?</h3>
        <Label htmlFor="tip-paid" className="text-sm">Total you paid</Label>
        <Input id="tip-paid" inputMode="decimal" className="max-w-xs" placeholder={money(input.bill + input.tax + 10).replace(/[^0-9.,]/g, "")} value={paid} onChange={(e) => setPaid(e.target.value)} />
        {rev && (rev.ok
          ? <p className="text-sm" data-testid="tip-implied">You left {money(rev.tip)}, which is <strong>{rev.pct}%</strong> of {input.tipOnPreTax ? "the bill before tax" : "the bill plus tax"}.</p>
          : <p role="alert" className="text-sm text-destructive" data-testid="tip-implied-error">{rev.message}</p>)}
      </section>

      <CalcSteps steps={steps} />
      <ExportBar gate={gate} text={text} csv={tableToCsv(strip, cols)} filename="tip-comparison.csv" label="result" />
      <CalcHistory tool="tip-calculator" />
    </div>
  );
}
